import type { Column, GridApi } from 'ag-grid-community';

type VisibleColumn = {
  getColId(): string;
  isVisible(): boolean;
};

export function resolveGridColumnFieldId(column: Pick<Column, 'getColDef' | 'getColId'>): string | null {
  const colDefField = column.getColDef().field;
  if (typeof colDefField === 'string' && colDefField.trim().length > 0) {
    return colDefField;
  }
  const colId = column.getColId();
  if (typeof colId === 'string' && colId.trim().length > 0) {
    return colId;
  }
  return null;
}

export function getVisibleSelectableColumnIds<RowData>(
  api: GridApi<RowData>,
  options?: { excludedColIds?: readonly string[] },
): string[] {
  const excludedColIds = new Set(options?.excludedColIds ?? []);
  const typedApi = api as GridApi<RowData> & { getColumns?: () => VisibleColumn[] };
  if (typeof typedApi.getColumns !== 'function') {
    return [];
  }
  return (typedApi.getColumns() ?? [])
    .filter(column => column.isVisible() && !excludedColIds.has(column.getColId()))
    .map(column => column.getColId());
}

export function moveFocusRelative<RowData>(
  api: GridApi<RowData> | null,
  columnId: string | null | undefined,
  currentRowIndex: number | null | undefined,
  rowDelta: number,
  colDelta: number,
  options?: { excludedColIds?: readonly string[] },
): boolean {
  if (!api || !columnId) {
    return false;
  }
  const rowCount = api.getDisplayedRowCount();
  if (rowCount <= 0) {
    return false;
  }
  const visibleColumns = getVisibleSelectableColumnIds(api, options);
  if (visibleColumns.length === 0) {
    return false;
  }
  const currentColumnIndex = visibleColumns.indexOf(columnId);
  let targetColumnIndex = currentColumnIndex;
  if (colDelta !== 0) {
    if (currentColumnIndex === -1) {
      return false;
    }
    targetColumnIndex = currentColumnIndex + colDelta;
    if (targetColumnIndex < 0 || targetColumnIndex >= visibleColumns.length) {
      return false;
    }
  } else if (targetColumnIndex === -1) {
    targetColumnIndex = 0;
  }

  const targetColumnId = visibleColumns[targetColumnIndex];
  let nextRowIndex = typeof currentRowIndex === 'number' ? currentRowIndex : 0;
  nextRowIndex = Math.min(Math.max(nextRowIndex + rowDelta, 0), rowCount - 1);
  api.clearCellSelection();
  api.addCellRange({ rowStartIndex: nextRowIndex, rowEndIndex: nextRowIndex, columns: [targetColumnId] });
  api.setFocusedCell(nextRowIndex, targetColumnId);
  api.ensureIndexVisible(nextRowIndex);
  return true;
}
